import React, { memo, useEffect, useState, useMemo } from 'react';
import { Strategy } from 'src/api/types';
import { useColumns } from './useColumns';
import GenericTable, { genEmtyRowTextComponent, GTRowEmpty, TableSheetRmp } from 'src/components/GenericTable';
import { Spacer, TableSkeleton } from 'src/common';
import _, { isEqual } from 'lodash';
import Button from 'src/components/form/Button';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { AddRmpModal } from '../../../AddRmpModal';
import { checkIsModifierStrategy } from 'src/components/Modifier/util';
import { SuggestModifiersChecker } from 'src/components/modals/comps';
import { useControlsQuery } from 'src/graphql/_generated';
import { useScenarioLinkedControlsCheckQuery } from '../useScenarioLinkedControlsCheckQuery';

import { isQueryLoading } from 'src/graphql';

interface FromExistingProps {
  scenarioId: string;
  setChecked: (a: string[]) => void;
  isLoading: boolean;
  onSubmit: () => void;
  addRecommendedModifiers: boolean;
  setAddRecommendedModifiers: (a: boolean) => void;
}

export const FromExisting: React.FC<FromExistingProps> = memo(
  ({ scenarioId, setChecked, isLoading, onSubmit, addRecommendedModifiers, setAddRecommendedModifiers }) => {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    const { data, loading, networkStatus } = useControlsQuery();
    const isControlsLoading = isQueryLoading({ loading, data, networkStatus });

    const controls = _.orderBy(data?.controls, ['meta.createdAt'], ['desc']);

    const {
      data: scenarioControlsData,
      loading: scenarioControlsLoading,
      networkStatus: scenarioControlsNetworkStatus,
    } = useScenarioLinkedControlsCheckQuery(scenarioId);
    const isScenarioControlsLoading = isQueryLoading({
      loading: scenarioControlsLoading,
      data: scenarioControlsData,
      networkStatus: scenarioControlsNetworkStatus,
    });

    const linkedControlIds = useMemo(
      () =>
        scenarioControlsData?.scenario?.riskManagementPlans
          .map((plan) => plan.control?.id)
          .filter(Boolean) as string[] | undefined,
      [scenarioControlsData],
    );

    const { columns, GTColumnSelectAnchorExported, checked } = useColumns({
      data: controls,
      linkedControlIds: linkedControlIds || [],
      scenarioId,
    });

    useEffect(() => {
      setChecked(checked);
    }, [checked]);

    const isDisabledModSuggest = useMemo(() => {
      if (!checked.length) return true;
      return !controls
        .filter((el) => checked.includes(el.id))
        .some((el) => checkIsModifierStrategy(el.strategy as Strategy));
    }, [controls, checked]);

    useEffect(() => {
      if (isDisabledModSuggest && addRecommendedModifiers) {
        setAddRecommendedModifiers(false);
      }
    }, [isDisabledModSuggest, addRecommendedModifiers, setAddRecommendedModifiers]);

    const isAnyLoading = isControlsLoading || isScenarioControlsLoading;

    return (
      <>
        {isAddModalOpen && <AddRmpModal onClose={() => setIsAddModalOpen(false)} />}
        {!isAnyLoading ? (
          <>
            <GenericTable
              tableId={TableIds.scenarioLinkExistingControl}
              data={controls}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              searchable
              expandContent={(data) => <TableSheetRmp data={data} />}
              itemHeight={50}
              rowDisabled={(row) => (linkedControlIds || []).includes(row.id)}
              emptyRow={
                <GTRowEmpty
                  itemHeight={50}
                  text={genEmtyRowTextComponent({
                    onClick: () => setIsAddModalOpen(true),
                  })}
                />
              }
            />
            <SuggestModifiersChecker
              isDisabledModSuggest={isDisabledModSuggest}
              addRecommendedModifiers={addRecommendedModifiers}
              setAddRecommendedModifiers={setAddRecommendedModifiers}
            />
            <Spacer $px={30} />
            <Button
              disabled={isLoading || !checked.length}
              loading={isLoading}
              onClick={() => {
                onSubmit();
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Add',
                  modal: 'Link controls to scenario modal',
                  tags: ['SCENARIO', 'RMP'],
                });
              }}
              primary
              css="width: 100%;"
              data-cy="submit-btn"
            >
              + ADD
            </Button>
          </>
        ) : (
          <TableSkeleton />
        )}
      </>
    );
  },
  isEqual,
);
